import {
  GROUP_KINDS,
  NOWHERE,
  UNITS,
  blankExercise,
  blankWorkout,
  expr as exprSource,
  groupWithNext,
  mapExercise,
  mapGroup,
  mapWorkout,
  moveExercise,
  moveItem,
  moveLine,
  moveMember,
  moveWorkout,
  removeExercise,
  textProp,
  ungroup,
  withProp,
  type ExerciseNode,
  type ExerciseRef,
  type Expr,
  type GroupItem,
  type GroupKind,
  type Prop,
  type TemplateNode,
  type WorkoutNode,
} from '@gymcloud/mixedscript'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react'

import { cycleOf, dayOf, freeDayAfter, inDayOrder, number, scheduleOf } from '~/lib/template-tree'
import { weekdayName, weekdayNames } from '~/lib/weekdays'

import { NumberField, PanelRow, RowPopover } from './editor-fields'
import { Menu, MenuItem, MenuSub } from './editor-menu'
import { ExerciseCombobox } from './exercise-combobox'
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  CopyIcon,
  CycleIcon,
  DeleteIcon,
  LayersIcon,
  NoteIcon,
  PlusIcon,
  SnapIcon,
  SplitIcon,
  SwapIcon,
} from './icons'
import { BoardTemplate } from '~/lib/board-template'
import { defaultLine } from '~/lib/line-shape'
import { GROUP_LABEL, GROUP_TEXT } from '~/lib/blocks'
import { useSelectOnFocus } from '~/lib/select-on-focus'
import { LineRow, type LineAt } from './template-line'

/**
 * The template as a board: one column per workout, or — on a strict schedule — a week
 * calendar, its days named by the locale's own week. Every edit goes through the tree and
 * comes back out as canonical script, which is the artifact the editor keeps.
 */

export function TemplateBoard({
  node,
  onNode,
  names,
}: {
  node: TemplateNode
  onNode: (next: TemplateNode) => void
  names: readonly string[]
}) {
  const drag = useRef<Drag | null>(null)
  const strict = scheduleOf(node) === 'strict'
  const [pending, setPending] = useState<Pending | null>(null)

  // The focus a fresh row asked for lands when it mounts; the ask does not outlive the change.
  // It has to be cleared after that render and not during it — clearing it as the tree changes
  // would take the ask away in the very render the fresh row was waiting for.
  useEffect(() => {
    if (pending !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPending(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node])

  const start = (held: Drag) => {
    drag.current = held
  }

  const editWorkout = (index: number, change: (workout: WorkoutNode) => WorkoutNode) => {
    onNode(mapWorkout(node, index, change))
  }

  /** Where a dragged thing has been let go. Every drop lands on one of the tree moves. */
  const dropOn = (target: Drop) => {
    const held = drag.current
    drag.current = null
    if (held === null) {
      return
    }
    if (held.kind === 'workout' && target.kind === 'workout') {
      if (strict) {
        onNode(withDay(node, held.workout, target.workout))
      } else {
        onNode(moveWorkout(node, held.workout, target.workout))
      }
      return
    }
    if (held.kind === 'workout' && target.kind === 'day') {
      onNode(onDay(node, held.workout, target.day))
      return
    }
    if (held.kind === 'exercise' && target.kind === 'exercise') {
      const from = held.ref
      const to = target.ref
      // Two members of one group reorder inside it; anything else is a move between items.
      if (from.workout === to.workout && from.item === to.item && from.within !== to.within) {
        editWorkout(from.workout, (workout) =>
          moveMember(workout, from.item, from.within, to.within),
        )
      } else {
        onNode(moveExercise(node, from, to.workout, to.item))
      }
      return
    }
    if (held.kind === 'exercise' && target.kind === 'column') {
      const into = node.workouts[target.workout]
      onNode(moveExercise(node, held.ref, target.workout, into?.items.length ?? 0))
      return
    }
    if (held.kind === 'line' && target.kind === 'line') {
      const from = held.at
      const to = target.at
      if (from.workout === to.workout && from.item === to.item && from.within === to.within) {
        editWorkout(from.workout, (workout) =>
          moveLine(workout, from.item, from.within, from.line, to.line),
        )
      }
    }
  }

  return (
    <DragContext.Provider value={{ start, dropOn }}>
      <FocusContext.Provider value={{ pending, ask: setPending }}>
        <BoardTemplate.Provider value={node}>
          {strict ? (
            <StrictCalendar node={node} onNode={onNode} names={names} />
          ) : (
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:pb-3">
              {node.workouts.map((workout, index) => (
                <WorkoutColumn
                  key={index}
                  workout={workout}
                  index={index}
                  node={node}
                  onNode={onNode}
                  names={names}
                  // A sequential template is worked through in order, so the order is worth showing.
                  badge={scheduleOf(node) === 'sequential' ? String(index + 1) : null}
                />
              ))}
              <button
                type="button"
                className="flex min-h-24 items-center justify-center gap-2 rounded-3xl border border-dashed border-white/15 px-6 py-4 text-base text-ink-muted transition-colors hover:border-white/30 hover:bg-hover hover:text-ink lg:sticky lg:top-0 lg:w-96 lg:shrink-0"
                onClick={() => {
                  setPending({ kind: 'workout', workout: node.workouts.length })
                  onNode({
                    ...node,
                    workouts: [
                      ...node.workouts,
                      blankWorkout(`Day ${String(node.workouts.length + 1)}`),
                    ],
                  })
                }}
              >
                <PlusIcon aria-hidden className="size-6" />
                Add workout
              </button>
            </div>
          )}
        </BoardTemplate.Provider>
      </FocusContext.Provider>
    </DragContext.Provider>
  )
}

/**
 * The cycle as the weeks it runs for: the locale's week across, day zero on its first day,
 * and one row per week of the cycle.
 */
function StrictCalendar({
  node,
  onNode,
  names,
}: {
  node: TemplateNode
  onNode: (next: TemplateNode) => void
  names: readonly string[]
}) {
  const cycle = Math.max(1, Math.min(90, cycleOf(node) ?? 7))
  const weeks = Math.ceil(cycle / 7)
  const labels = weekdayNames()

  // A cycle that shrank can leave a workout past its end. It is still in the template, so it is
  // shown below the calendar rather than quietly dropped out of sight.
  const loose = node.workouts
    .map((workout, index) => ({ workout, index, day: dayOf(workout) }))
    .filter(({ day }) => day === null || day < 0 || day >= cycle)

  return (
    <div className="space-y-8">
      <div className="lg:pb-3">
        {/* A day's column is a workout card like any other, so it is the width of one — and
            takes what its card holds, since a track that cannot grow is two cards meeting.
            The width has to be the track's own size: as a ceiling it would only ever be
            spent out of room left over, and a week of columns leaves none. */}
        <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[2.5rem_repeat(7,max-content)] lg:gap-3">
          <span aria-hidden className="hidden lg:block" />
          {labels.map((label) => (
            <span
              key={label}
              aria-hidden
              className="hidden text-xs font-medium uppercase tracking-wide text-ink-muted lg:block lg:px-2"
            >
              {label}
            </span>
          ))}
          {Array.from({ length: weeks }, (_, week) => (
            <CalendarWeek
              key={week}
              week={week}
              weeks={weeks}
              cycle={cycle}
              node={node}
              onNode={onNode}
              names={names}
            />
          ))}
        </div>
      </div>

      {loose.length === 0 ? null : (
        <section aria-label="Not on a day yet" className="space-y-3">
          <h3 className="text-lg font-medium">Not on a day yet</h3>
          <p className="text-sm text-ink-muted">
            The cycle is shorter than the day these were on. Drag one onto a day, or give the cycle
            more weeks.
          </p>
          {/* Wrapped rather than scrolled, so nothing here — menus most of all — gets clipped. */}
          <div className="flex flex-col gap-5 lg:flex-row lg:flex-wrap lg:items-start">
            {loose.map(({ workout, index }) => (
              <WorkoutColumn
                key={index}
                workout={workout}
                index={index}
                node={node}
                onNode={onNode}
                names={names}
                badge="Not on a day"
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function CalendarWeek({
  week,
  weeks,
  cycle,
  node,
  onNode,
  names,
}: {
  week: number
  weeks: number
  cycle: number
  node: TemplateNode
  onNode: (next: TemplateNode) => void
  names: readonly string[]
}) {
  return (
    <>
      <span
        aria-hidden
        className="hidden pt-3 text-xs font-medium uppercase tracking-wide text-ink-muted lg:block"
      >
        {weeks > 1 ? `W${String(week + 1)}` : ''}
      </span>
      {Array.from({ length: 7 }, (_, weekday) => {
        const day = week * 7 + weekday
        if (day >= cycle) {
          return <div key={day} aria-hidden className="hidden lg:block" />
        }
        const index = node.workouts.findIndex((workout) => dayOf(workout) === day)
        const workout = node.workouts[index]
        if (workout === undefined) {
          return <RestDay key={day} day={day} weeks={weeks} node={node} onNode={onNode} />
        }
        return (
          <WorkoutColumn
            key={day}
            workout={workout}
            index={index}
            node={node}
            onNode={onNode}
            names={names}
            badge={dayBadge(day, weeks)}
            day={day}
            inGrid
          />
        )
      })}
    </>
  )
}

/** The name a workout's day wears on its card: the weekday, and the week when there are more. */
function dayBadge(day: number, weeks: number): string {
  return weeks > 1 ? `${weekdayName(day)} · W${String(Math.floor(day / 7) + 1)}` : weekdayName(day)
}

/** A day of the cycle with nothing on it. It is rest, and pressing anywhere on it is a workout. */
function RestDay({
  day,
  weeks,
  node,
  onNode,
}: {
  day: number
  weeks: number
  node: TemplateNode
  onNode: (next: TemplateNode) => void
}) {
  const { dropOn } = useDrag()
  const { ask } = useFocus()
  const { over, targetProps } = useDropTarget(() => {
    dropOn({ kind: 'day', day })
  })
  return (
    <button
      type="button"
      aria-label={`Add a workout on ${weekdayName(day)}`}
      className={`group w-full rounded-3xl border border-dashed p-4 text-ink-muted transition-colors lg:min-w-96 ${
        over ? 'border-accent bg-accent/10' : 'border-white/10 hover:border-white/25 hover:bg-hover'
      }`}
      onClick={() => {
        ask({ kind: 'workout', workout: node.workouts.length })
        onNode({
          ...node,
          workouts: [...node.workouts, withProp(blankWorkout(''), 'day', number(day, UNITS.reps))],
        })
      }}
      {...targetProps}
    >
      {/* The cell runs the height of its week; its words ride along the scroll inside it. */}
      <span className="flex min-h-24 flex-col items-center justify-center gap-1 lg:sticky lg:top-4">
        <span className="text-xs uppercase tracking-wide">{dayBadge(day, weeks)}</span>
        <span className="text-sm group-hover:hidden">Rest</span>
        <span className="hidden items-center gap-1 text-sm text-ink group-hover:flex">
          <PlusIcon aria-hidden className="size-4" />
          Add a workout
        </span>
      </span>
    </button>
  )
}

function WorkoutColumn({
  workout,
  index,
  node,
  onNode,
  names,
  badge,
  day,
  inGrid,
}: {
  workout: WorkoutNode
  index: number
  node: TemplateNode
  onNode: (next: TemplateNode) => void
  names: readonly string[]
  badge: string | null
  day?: number
  /** A calendar cell fills its day; a free column keeps its own width. */
  inGrid?: boolean
}) {
  const { start, dropOn } = useDrag()
  const { pending, ask } = useFocus()
  const select = useSelectOnFocus()
  const strict = scheduleOf(node) === 'strict'
  const cycle = Math.max(1, cycleOf(node) ?? 7)
  const notes = textProp(workout.props, 'notes')
  const [notesOpen, setNotesOpen] = useState(false)
  /*
   * Something the card was asked for and cannot do. It is said on the card rather than
   * somewhere central, because the card is what was asked and where the eye already is.
   */
  const [problem, setProblem] = useState<string | null>(null)
  const onProblem = (said: string) => {
    setProblem(said)
    window.setTimeout(() => {
      setProblem(null)
    }, PROBLEM_MS)
  }
  const edit = (change: (held: WorkoutNode) => WorkoutNode) => {
    onNode(mapWorkout(node, index, change))
  }
  const { over, targetProps } = useDropTarget(() => {
    dropOn({ kind: 'column', workout: index })
  })

  const addItem = (item: WorkoutNode['items'][number], focus: ExerciseRef | null) => {
    if (focus !== null) {
      ask({ kind: 'exercise', ref: focus })
    }
    edit((held) => ({ ...held, items: [...held.items, item] }))
  }

  const earlierBlocked = strict ? day === undefined || day <= 0 : index === 0
  const laterBlocked = strict
    ? day === undefined || day >= cycle - 1
    : index === node.workouts.length - 1

  return (
    <section
      id={`board-workout-${String(index)}`}
      aria-label={workout.name === '' ? 'Workout' : workout.name}
      // A card is a column's width to start with, and takes more when a line it holds asks
      // for it — a set never folds onto a second line to keep the card narrow.
      className={`flex flex-col rounded-3xl bg-surface-raised transition-shadow ${
        inGrid === true
          ? 'w-full min-w-max self-start lg:min-w-96'
          : 'w-max min-w-full lg:min-w-96 lg:shrink-0 lg:self-start'
      } ${over ? 'ring-1 ring-accent' : ''}`}
      {...targetProps}
    >
      <header
        draggable
        className="flex cursor-grab items-center gap-1 px-2 py-2 active:cursor-grabbing"
        onDragStart={() => {
          start({ kind: 'workout', workout: index })
        }}
        onDragOver={(event) => {
          event.preventDefault()
        }}
        onDrop={(event) => {
          event.stopPropagation()
          dropOn({ kind: 'workout', workout: index })
        }}
      >
        {badge === null ? null : (
          <span className="shrink-0 rounded-full bg-surface-bright px-2 py-0.5 text-xs text-ink-muted">
            {badge}
          </span>
        )}
        <input
          aria-label="Workout"
          value={workout.name}
          placeholder="Workout"
          autoFocus={pending?.kind === 'workout' && pending.workout === index}
          className="min-w-0 flex-1 rounded-lg bg-transparent px-2 py-1 text-lg font-medium outline-none transition-colors placeholder:text-ink-muted hover:bg-hover focus:bg-surface-bright"
          onFocus={select.onFocus}
          onMouseUp={select.onMouseUp}
          onChange={(event) => {
            const name = event.target.value
            edit((held) => ({ ...held, name }))
          }}
        />
        <Menu label="Workout options">
          {/* Everything the card can take, without scrolling to the foot of it to reach them. */}
          <MenuSub label="Add" icon={<PlusIcon className="size-4" />}>
            <MenuItem
              icon={<PlusIcon className="size-4 text-block-exercise" />}
              onSelect={() => {
                addItem(
                  { type: 'exercise', exercise: blankExercise('') },
                  { workout: index, item: workout.items.length, within: 0 },
                )
              }}
            >
              Exercise
            </MenuItem>
            {GROUP_KINDS.map((kind) => (
              <MenuItem
                key={kind}
                icon={<GroupIcon kind={kind} className={`size-4 ${GROUP_TEXT[kind]}`} />}
                onSelect={() => {
                  addItem(freshGroup(kind), {
                    workout: index,
                    item: workout.items.length,
                    within: 0,
                  })
                }}
              >
                {ADD_LABEL[kind]}
              </MenuItem>
            ))}
          </MenuSub>
          {/* Any free day is one pick away — a drag, or six clicks of earlier, never required. */}
          {strict && day !== undefined
            ? (() => {
                const weeks = Math.ceil(cycle / 7)
                const free = Array.from({ length: cycle }, (_, held) => held).filter(
                  (candidate) =>
                    candidate !== day && !node.workouts.some((other) => dayOf(other) === candidate),
                )
                if (free.length === 0) {
                  return null
                }
                const items = free.map((candidate) => (
                  <MenuItem
                    key={candidate}
                    icon={<ArrowRightIcon className="size-4" />}
                    onSelect={() => {
                      onNode(onDay(node, index, candidate))
                    }}
                  >
                    {dayBadge(candidate, weeks)}
                  </MenuItem>
                ))
                // One free day is a choice already made; a list of them is a choice to open.
                if (free.length === 1) {
                  const only = free[0] ?? 0
                  return (
                    <MenuItem
                      icon={<ArrowRightIcon className="size-4" />}
                      onSelect={() => {
                        onNode(onDay(node, index, only))
                      }}
                    >
                      Move to {dayBadge(only, weeks)}
                    </MenuItem>
                  )
                }
                return (
                  <MenuSub label="Move to" icon={<ArrowRightIcon className="size-4" />}>
                    {items}
                  </MenuSub>
                )
              })()
            : null}
          {/* A workout the calendar lost still has a way back that is not a drag. */}
          {strict && day === undefined
            ? (() => {
                const free = Array.from({ length: cycle }, (_, held) => held).find(
                  (candidate) => !node.workouts.some((other) => dayOf(other) === candidate),
                )
                return (
                  <MenuItem
                    icon={<ArrowRightIcon className="size-4" />}
                    disabled={free === undefined}
                    onSelect={() => {
                      if (free !== undefined) {
                        onNode(onDay(node, index, free))
                      }
                    }}
                  >
                    Put on the first free day
                  </MenuItem>
                )
              })()
            : null}
          <MenuItem
            icon={<ArrowLeftIcon className="size-4" />}
            disabled={earlierBlocked}
            onSelect={() => {
              if (strict && day !== undefined) {
                onNode(onDay(node, index, day - 1))
              } else {
                onNode(moveWorkout(node, index, index - 1))
              }
            }}
          >
            Move earlier
          </MenuItem>
          <MenuItem
            icon={<ArrowRightIcon className="size-4" />}
            disabled={laterBlocked}
            onSelect={() => {
              if (strict && day !== undefined) {
                onNode(onDay(node, index, day + 1))
              } else {
                onNode(moveWorkout(node, index, index + 1))
              }
            }}
          >
            Move later
          </MenuItem>
          <MenuItem
            icon={<CopyIcon className="size-4" />}
            onSelect={() => {
              const copied = duplicateWorkout(node, index)
              if (copied === null) {
                // A strict cycle with no day left has nowhere to put a copy, and a workout off
                // the calendar is one that never comes round.
                onProblem('Every day of the cycle is taken')
                return
              }
              onNode(copied.node)
              // The two things a copy needs are a day of its own and a name that is not
              // "copy", so it opens on the name rather than waiting to be found later.
              ask({ kind: 'workout', workout: copied.at })
            }}
          >
            Duplicate workout
          </MenuItem>
          <MenuItem
            icon={<NoteIcon className="size-4" />}
            checked={notes !== null || notesOpen}
            onSelect={() => {
              if (notes !== null) {
                edit((held) => withProp(held, 'notes', null))
                setNotesOpen(false)
              } else {
                setNotesOpen(!notesOpen)
              }
            }}
          >
            Notes
          </MenuItem>
          <MenuItem
            icon={<DeleteIcon className="size-4" />}
            danger
            onSelect={() => {
              onNode({
                ...node,
                workouts: node.workouts.filter((_, position) => position !== index),
              })
            }}
          >
            Remove workout
          </MenuItem>
        </Menu>
      </header>

      {problem === null ? null : (
        <p role="status" className="px-4 pb-2 text-xs text-danger">
          {problem}
        </p>
      )}
      {notes !== null || notesOpen ? (
        <div className="px-3 py-2">
          <input
            aria-label="Workout notes"
            value={notes ?? ''}
            autoFocus={notesOpen}
            placeholder="Notes for this workout"
            className="w-full rounded-lg bg-transparent px-1 py-0.5 text-sm text-ink-muted outline-none placeholder:text-ink-muted/60 focus:bg-surface-bright focus:text-ink"
            onChange={(event) => {
              const value = event.target.value
              edit((held) => withProp(held, 'notes', value === '' ? null : text(value)))
            }}
            onBlur={() => {
              setNotesOpen(false)
            }}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5 p-2.5">
        {workout.items.map((item, itemIndex) =>
          item.type === 'exercise' ? (
            <ExerciseBlock
              key={itemIndex}
              exercise={item.exercise}
              at={{ workout: index, item: itemIndex, within: 0 }}
              node={node}
              onNode={onNode}
              names={names}
            />
          ) : (
            <GroupBlock
              key={itemIndex}
              group={item}
              workout={index}
              item={itemIndex}
              node={node}
              onNode={onNode}
              names={names}
            />
          ),
        )}

        <AddBar
          empty={workout.items.length === 0}
          onExercise={() => {
            addItem(
              { type: 'exercise', exercise: blankExercise('') },
              { workout: index, item: workout.items.length, within: 0 },
            )
          }}
          onGroup={(kind) => {
            addItem(freshGroup(kind), { workout: index, item: workout.items.length, within: 0 })
          }}
        />
      </div>
    </section>
  )
}

/** The four things a workout can take, spelled out and wearing their colours. */
function AddBar({
  empty,
  onExercise,
  onGroup,
}: {
  empty: boolean
  onExercise: () => void
  onGroup: (kind: GroupKind) => void
}) {
  return (
    <div className="space-y-1 pt-1">
      {empty ? <p className="px-2 text-sm text-ink-muted">Nothing here yet. Add:</p> : null}
      <div className="grid grid-cols-2 gap-1">
        <AddButton
          label="Exercise"
          color="text-block-exercise"
          icon={<PlusIcon aria-hidden className="size-4" />}
          onPress={onExercise}
        />
        {GROUP_KINDS.map((kind) => (
          <AddButton
            key={kind}
            label={ADD_LABEL[kind]}
            color={GROUP_TEXT[kind]}
            icon={<GroupIcon kind={kind} className="size-4" />}
            title={GROUP_HINT[kind]}
            onPress={() => {
              onGroup(kind)
            }}
          />
        ))}
      </div>
    </div>
  )
}

/** Only the icon wears the block's colour; the words lean into the hover instead. */
function AddButton({
  label,
  color,
  icon,
  title,
  onPress,
}: {
  label: string
  color: string
  icon: ReactNode
  title?: string
  onPress: () => void
}) {
  return (
    <button
      type="button"
      title={title}
      className="group flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs text-ink-muted transition-colors hover:bg-hover hover:text-ink"
      onClick={onPress}
    >
      <span className={color}>{icon}</span>
      <span className="transition-transform duration-150 group-hover:translate-x-0.5">{label}</span>
    </button>
  )
}

/** What a group is called while it is being added, which reads differently from its heading. */
const ADD_LABEL: Record<GroupKind, string> = {
  superset: 'Superset',
  choose: 'Alternatives',
  rotate: 'Rotation',
}

const GROUP_HINT: Record<GroupKind, string> = {
  superset: 'Do one set of each in turn, then go round again',
  choose: 'Only one of these gets done — you can swap between them whenever you like',
  rotate: 'A different one each session, taking turns',
}

export function GroupIcon({ kind, className }: { kind: GroupKind; className?: string }) {
  const Icon = kind === 'superset' ? LayersIcon : kind === 'choose' ? SwapIcon : CycleIcon
  return <Icon aria-hidden className={className} />
}

/** A superset, a choose or a rotate: its members inset in a block wearing the kind's colour. */
function GroupBlock({
  group,
  workout,
  item,
  node,
  onNode,
  names,
}: {
  group: GroupItem
  workout: number
  item: number
  node: TemplateNode
  onNode: (next: TemplateNode) => void
  names: readonly string[]
}) {
  const { ask } = useFocus()
  const kind = group.kind
  const edit = (change: (held: WorkoutNode) => WorkoutNode) => {
    onNode(mapWorkout(node, workout, change))
  }

  return (
    <div
      role="group"
      aria-label={GROUP_LABEL[kind]}
      className="space-y-1.5 rounded-2xl bg-surface-overlay p-2"
    >
      {/* What the block is, and what can be done to it, on one line; what it holds beneath. */}
      <div className="space-y-1 px-1 py-0.5">
        <div className="flex items-center gap-2">
          <span
            title={GROUP_HINT[kind]}
            className={`cursor-help text-xs font-medium uppercase tracking-wide ${GROUP_TEXT[kind]}`}
          >
            {GROUP_LABEL[kind]}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            className="group flex items-center gap-1 rounded-full px-2 py-1 text-xs text-ink-muted transition-colors hover:bg-hover hover:text-ink"
            onClick={() => {
              ask({ kind: 'exercise', ref: { workout, item, within: group.exercises.length } })
              edit((held) =>
                mapGroup(held, item, (found) => ({
                  ...found,
                  exercises: [...found.exercises, blankExercise('')],
                })),
              )
            }}
          >
            <PlusIcon aria-hidden className={`size-4 ${GROUP_TEXT[kind]}`} />
            <span className="transition-transform duration-150 group-hover:translate-x-0.5">
              {kind === 'choose' ? 'Alternative' : 'Exercise'}
            </span>
          </button>
          <Menu label={`${GROUP_LABEL[kind]} options`}>
            <MenuItem
              icon={<SplitIcon className="size-4" />}
              onSelect={() => {
                edit((held) => ungroup(held, item))
              }}
            >
              Ungroup
            </MenuItem>
            <MenuItem
              icon={<CopyIcon className="size-4" />}
              onSelect={() => {
                edit((held) => ({
                  ...held,
                  items: [...held.items.slice(0, item + 1), group, ...held.items.slice(item + 1)],
                }))
              }}
            >
              Duplicate {GROUP_LABEL[kind].toLowerCase()}
            </MenuItem>
            <MenuItem
              icon={<DeleteIcon className="size-4" />}
              danger
              onSelect={() => {
                edit((held) => ({
                  ...held,
                  items: held.items.filter((_, position) => position !== item),
                }))
              }}
            >
              Remove group and its exercises
            </MenuItem>
          </Menu>
        </div>
        {kind === 'superset' ? (
          <label className="flex items-center gap-1.5 px-1 text-xs text-ink-muted">
            Rest between rounds
            <NumberField
              clearable
              label="Rest between rounds"
              value={((held) => (held ? exprSource(held) : ''))(
                group.props.find((prop) => prop.name === 'rest')?.value,
              )}
              unit={UNITS.seconds}
              onValue={(value) => {
                edit((held) => mapGroup(held, item, (found) => withProp(found, 'rest', value)))
              }}
            />
          </label>
        ) : null}
      </div>

      {group.exercises.map((exercise, within) => (
        <ExerciseBlock
          key={within}
          exercise={exercise}
          at={{ workout, item, within }}
          node={node}
          onNode={onNode}
          names={names}
          inSuperset={kind === 'superset'}
        />
      ))}
    </div>
  )
}

function ExerciseBlock({
  exercise,
  at,
  node,
  onNode,
  names,
  inSuperset,
}: {
  exercise: ExerciseNode
  at: ExerciseRef
  node: TemplateNode
  onNode: (next: TemplateNode) => void
  names: readonly string[]
  inSuperset?: boolean
}) {
  const { start, dropOn } = useDrag()
  const { pending, ask } = useFocus()
  const workout = node.workouts[at.workout]
  const item = workout?.items[at.item]
  const grouped = item?.type === 'group'
  const groupSize = item?.type === 'group' ? item.exercises.length : 0
  const itemCount = workout?.items.length ?? 0

  // The names this exercise's combobox offers: the catalog, plus what the template already
  // says elsewhere — an exercise used two cards over is known, the one being typed is not.
  const known = useMemo(() => {
    const all = new Set(names)
    node.workouts.forEach((held, workoutIndex) => {
      held.items.forEach((other, itemIndex) => {
        const members = other.type === 'exercise' ? [other.exercise] : other.exercises
        members.forEach((member, within) => {
          const self =
            workoutIndex === at.workout &&
            itemIndex === at.item &&
            (other.type === 'exercise' || within === at.within)
          const name = member.name.trim()
          if (!self && name !== '') {
            all.add(name)
          }
        })
      })
    })
    return [...all].sort((a, b) => a.localeCompare(b))
  }, [names, node, at.workout, at.item, at.within])

  const edit = (change: (held: ExerciseNode) => ExerciseNode) => {
    onNode(mapWorkout(node, at.workout, (held) => mapExercise(held, at.item, at.within, change)))
  }
  const editWorkout = (change: (held: WorkoutNode) => WorkoutNode) => {
    onNode(mapWorkout(node, at.workout, change))
  }
  const { over, targetProps } = useDropTarget(() => {
    dropOn({ kind: 'exercise', ref: at })
  })

  const next = workout?.items[at.item + 1]
  // Where an exercise can hop to: neighbours by position — or, under strict, by day, so
  // "next" never walks backwards across the calendar.
  const strictBoard = scheduleOf(node) === 'strict'
  const ordered = node.workouts
    .map((held, position) => ({ position, day: dayOf(held) }))
    .sort((a, b) => (strictBoard ? (a.day ?? 999) - (b.day ?? 999) : a.position - b.position))
  const elsewhere = ordered
    .filter((held) => held.position !== at.workout)
    .map((held) => held.position)
  const workoutLabel = (position: number | undefined): string => {
    if (position === undefined) {
      return ''
    }
    const name = node.workouts[position]?.name.trim() ?? ''
    return name === '' ? 'the unnamed workout' : `“${name}”`
  }
  const focused =
    pending?.kind === 'exercise' &&
    pending.ref.workout === at.workout &&
    pending.ref.item === at.item &&
    pending.ref.within === at.within
  // The gear in the heading opens the same details the chips do, for when no chip is showing.
  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <article
      id={`board-ex-${String(at.workout)}-${String(at.item)}-${String(at.within)}`}
      className={`rounded-2xl bg-surface-bright/40 transition-shadow ${over ? 'ring-1 ring-accent' : ''}`}
      {...targetProps}
    >
      {/* The heading answers to the pointer the way a set line does — same hover, same box. */}
      <div
        draggable
        className="flex cursor-grab items-center gap-1 rounded-xl px-1 py-1 transition-colors hover:bg-hover active:cursor-grabbing"
        onDragStart={(event) => {
          event.stopPropagation()
          start({ kind: 'exercise', ref: at })
        }}
      >
        <ExerciseCombobox
          value={exercise.name}
          names={known}
          autoFocus={focused}
          onValue={(name) => {
            edit((held) => ({ ...held, name }))
          }}
        />
        <ExerciseDetails
          exercise={exercise}
          open={detailsOpen}
          onOpen={setDetailsOpen}
          edit={edit}
        />
        <Menu label={`Options for ${exercise.name === '' ? 'this exercise' : exercise.name}`}>
          <MenuItem
            icon={<ArrowUpIcon className="size-4" />}
            disabled={grouped ? at.within === 0 : at.item === 0}
            onSelect={() => {
              if (grouped) {
                editWorkout((held) => moveMember(held, at.item, at.within, at.within - 1))
              } else {
                editWorkout((held) => moveItem(held, at.item, at.item - 1))
              }
            }}
          >
            Move up
          </MenuItem>
          <MenuItem
            icon={<ArrowDownIcon className="size-4" />}
            disabled={grouped ? at.within === groupSize - 1 : at.item === itemCount - 1}
            onSelect={() => {
              if (grouped) {
                editWorkout((held) => moveMember(held, at.item, at.within, at.within + 1))
              } else {
                editWorkout((held) => moveItem(held, at.item, at.item + 1))
              }
            }}
          >
            Move down
          </MenuItem>
          {/* Every other workout, in the order the board reads them, and not one hop at a time. */}
          {elsewhere.length > 0
            ? (() => {
                const moveTo = (position: number) => () => {
                  const into = node.workouts[position]
                  onNode(moveExercise(node, at, position, into?.items.length ?? 0))
                }
                // One other workout is a choice already made; several are a choice to open.
                if (elsewhere.length === 1) {
                  const only = elsewhere[0] ?? 0
                  return (
                    <MenuItem icon={<ArrowRightIcon className="size-4" />} onSelect={moveTo(only)}>
                      Move to {workoutLabel(only)}
                    </MenuItem>
                  )
                }
                return (
                  <MenuSub label="Move to" icon={<ArrowRightIcon className="size-4" />}>
                    {elsewhere.map((position) => (
                      <MenuItem
                        key={position}
                        icon={<ArrowRightIcon className="size-4" />}
                        onSelect={moveTo(position)}
                      >
                        {workoutLabel(position)}
                      </MenuItem>
                    ))}
                  </MenuSub>
                )
              })()
            : null}
          {/* Offered only when there is a next exercise to pair with. */}
          {!grouped && next?.type === 'exercise' ? (
            <MenuItem
              icon={<LayersIcon className="size-4" />}
              onSelect={() => {
                editWorkout((held) => groupWithNext(held, at.item))
              }}
            >
              Superset with next exercise
            </MenuItem>
          ) : null}
          {!grouped ? (
            <MenuItem
              icon={<SwapIcon className="size-4" />}
              onSelect={() => {
                ask({ kind: 'exercise', ref: { workout: at.workout, item: at.item, within: 1 } })
                editWorkout((held) => wrapAlternative(held, at.item))
              }}
            >
              Add an alternative
            </MenuItem>
          ) : null}
          {grouped ? (
            <MenuItem
              icon={<SplitIcon className="size-4" />}
              onSelect={() => {
                onNode(moveExercise(node, at, at.workout, at.item + 1))
              }}
            >
              Move out of the group
            </MenuItem>
          ) : null}
          <MenuItem
            icon={<CopyIcon className="size-4" />}
            onSelect={() => {
              if (grouped) {
                editWorkout((held) =>
                  mapGroup(held, at.item, (found) => ({
                    ...found,
                    exercises: [
                      ...found.exercises.slice(0, at.within + 1),
                      exercise,
                      ...found.exercises.slice(at.within + 1),
                    ],
                  })),
                )
              } else {
                editWorkout((held) => ({
                  ...held,
                  items: [
                    ...held.items.slice(0, at.item + 1),
                    { type: 'exercise', exercise },
                    ...held.items.slice(at.item + 1),
                  ],
                }))
              }
            }}
          >
            Duplicate exercise
          </MenuItem>
          <MenuItem
            icon={<DeleteIcon className="size-4" />}
            danger
            onSelect={() => {
              editWorkout((held) => removeExercise(held, at.item, at.within))
            }}
          >
            Remove exercise
          </MenuItem>
        </Menu>
      </div>

      <ul className="flex flex-col">
        {exercise.lines.map((line, index) => {
          const lineAt: LineAt = { ...at, line: index }
          return (
            <li key={index}>
              <LineRow
                line={line}
                at={lineAt}
                lineCount={exercise.lines.length}
                restAllowed={inSuperset !== true}
                onLine={(changed) => {
                  edit((held) => ({
                    ...held,
                    lines: held.lines.map((existing, position) =>
                      position === index ? changed : existing,
                    ),
                  }))
                }}
                onMove={(to) => {
                  editWorkout((held) => moveLine(held, at.item, at.within, index, to))
                }}
                onRemove={() => {
                  edit((held) => ({
                    ...held,
                    lines: held.lines.filter((_, position) => position !== index),
                  }))
                }}
                onDuplicate={() => {
                  edit((held) => ({
                    ...held,
                    lines: [
                      ...held.lines.slice(0, index + 1),
                      line,
                      ...held.lines.slice(index + 1),
                    ],
                  }))
                }}
                onDragStart={() => {
                  start({ kind: 'line', at: lineAt })
                }}
                onDropOn={() => {
                  dropOn({ kind: 'line', at: lineAt })
                }}
                autoFocus={
                  pending?.kind === 'line' &&
                  pending.at.workout === at.workout &&
                  pending.at.item === at.item &&
                  pending.at.within === at.within &&
                  pending.at.line === index
                }
              />
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        className="group flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm text-ink-muted transition-colors hover:bg-hover hover:text-ink"
        onClick={() => {
          ask({ kind: 'line', at: { ...at, line: exercise.lines.length } })
          edit((held) => ({ ...held, lines: [...held.lines, defaultLine(held.lines)] }))
        }}
      >
        <PlusIcon aria-hidden className="size-4" />
        <span className="transition-transform duration-150 group-hover:translate-x-0.5">
          Add line
        </span>
      </button>
    </article>
  )
}

/**
 * What an exercise carries beyond its lines — its snap and its notes. Both ride in the exercise's
 * own heading, beside its menu: the snap as its mark and its value, the notes as their mark alone,
 * since a note long enough to be worth writing is too long to sit on a heading.
 *
 * Only what is set is shown, but the way in is always there: with nothing set it is the snap's mark
 * on its own, dim, which is the invitation to set one.
 */
function ExerciseDetails({
  exercise,
  open,
  onOpen,
  edit,
}: {
  exercise: ExerciseNode
  open: boolean
  onOpen: (open: boolean) => void
  edit: (change: (held: ExerciseNode) => ExerciseNode) => void
}) {
  const snap = propSource(exercise.props, 'snap')
  const notes = textProp(exercise.props, 'notes')
  const any = snap !== null || notes !== null

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`Details for ${exercise.name === '' ? 'this exercise' : exercise.name}`}
        className={`flex shrink-0 items-center gap-2 rounded-full px-2 py-1 text-xs transition-colors hover:bg-hover hover:text-ink ${
          open ? 'bg-white/10 text-ink' : any ? 'text-ink-muted' : 'text-ink-muted/50'
        }`}
        onClick={() => {
          onOpen(!open)
        }}
      >
        {snap === null && notes !== null ? null : (
          <span
            title="Snap — weights round to the nearest step of this"
            className="flex items-center gap-1"
          >
            <SnapIcon aria-hidden className="size-3.5 shrink-0" />
            {snap}
          </span>
        )}
        {notes !== null ? <NoteMark notes={notes} /> : null}
      </button>

      <RowPopover
        open={open}
        label={`Details for ${exercise.name === '' ? 'this exercise' : exercise.name}`}
        onClose={() => {
          onOpen(false)
        }}
      >
        <div className="scroll-y min-h-0 space-y-2.5 overscroll-contain p-3">
          <PanelRow label="Snap">
            <NumberField
              boxed
              clearable
              label="Snap"
              value={snap ?? ''}
              unit={UNITS.kg}
              onValue={(value) => {
                edit((held) => withProp(held, 'snap', value))
              }}
            />
            <p className="text-xs text-ink-muted">
              Weights round to the nearest step of this — 2.5kg suits most bars.
            </p>
          </PanelRow>
          <PanelRow label="Notes">
            <input
              aria-label="Exercise notes"
              value={notes ?? ''}
              placeholder="Anything this exercise should carry"
              className="w-full rounded-lg bg-surface-raised px-2 py-1 text-base text-ink outline-none focus:ring-1 focus:ring-accent"
              onChange={(event) => {
                const value = event.target.value
                edit((held) => withProp(held, 'notes', value === '' ? null : text(value)))
              }}
            />
          </PanelRow>
        </div>
      </RowPopover>
    </div>
  )
}

/** The mark a note wears on a heading. What it says is in the form; this only says there is one. */
function NoteMark({ notes }: { notes: string }) {
  return (
    <span title={notes} className="flex items-center">
      <NoteIcon aria-hidden className="size-3.5 shrink-0" />
    </span>
  )
}

// --- dragging ---

type Drag =
  | { readonly kind: 'workout'; readonly workout: number }
  | { readonly kind: 'exercise'; readonly ref: ExerciseRef }
  | { readonly kind: 'line'; readonly at: LineAt }

type Drop =
  | { readonly kind: 'workout'; readonly workout: number }
  | { readonly kind: 'day'; readonly day: number }
  | { readonly kind: 'column'; readonly workout: number }
  | { readonly kind: 'exercise'; readonly ref: ExerciseRef }
  | { readonly kind: 'line'; readonly at: LineAt }

interface Dragging {
  /** What has been picked up. The board holds it; nothing else may reach in and set it. */
  readonly start: (held: Drag) => void
  readonly dropOn: (target: Drop) => void
}

const DragContext = createContext<Dragging>({
  start: () => undefined,
  dropOn: () => undefined,
})

function useDrag(): Dragging {
  return useContext(DragContext)
}

/** A place a drag can land, which lights up while one is over it. */
function useDropTarget(onDrop: () => void) {
  const [over, setOver] = useState(false)
  const depth = useRef(0)

  // A drop taken by a nested target never reaches the ones around it, and a drag let go over
  // nothing at all reaches none of them — either way the highlight would be left lit. So the
  // end of a drag, wherever it lands, puts every target back out.
  useEffect(() => {
    if (!over) {
      return
    }
    const clear = () => {
      depth.current = 0
      setOver(false)
    }
    document.addEventListener('dragend', clear, true)
    document.addEventListener('drop', clear, true)
    return () => {
      document.removeEventListener('dragend', clear, true)
      document.removeEventListener('drop', clear, true)
    }
  }, [over])

  const targetProps = {
    onDragEnter: (event: DragEvent) => {
      event.preventDefault()
      depth.current += 1
      setOver(true)
    },
    onDragOver: (event: DragEvent) => {
      event.preventDefault()
    },
    onDragLeave: () => {
      depth.current -= 1
      if (depth.current <= 0) {
        depth.current = 0
        setOver(false)
      }
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      depth.current = 0
      setOver(false)
      onDrop()
    },
  }
  return { over, targetProps }
}

// --- who gets the focus a fresh row asked for ---

type Pending =
  | { readonly kind: 'workout'; readonly workout: number }
  | { readonly kind: 'exercise'; readonly ref: ExerciseRef }
  | { readonly kind: 'line'; readonly at: LineAt }

const FocusContext = createContext<{
  pending: Pending | null
  ask: (pending: Pending) => void
}>({ pending: null, ask: () => undefined })

function useFocus() {
  return useContext(FocusContext)
}

// --- reading and writing the tree ---

/** How long something the card could not do stays said before it goes on its own. */
const PROBLEM_MS = 4000

/** A copy made, and where it landed once the template was put back in order. */
interface Duplicated {
  readonly node: TemplateNode
  readonly at: number
}

/**
 * A workout copied whole, its copy landing right after it, the way duplicating an exercise or
 * a line does.
 *
 * On a strict schedule the copy cannot share the original's day, so it takes the next one
 * nothing is on. It cannot go without a day either — a strict template is its calendar, and a
 * workout off the calendar is a workout that never comes round — so a cycle with no day left
 * is a duplication that does not happen, and says so. Null is that refusal.
 */
function duplicateWorkout(node: TemplateNode, index: number): Duplicated | null {
  const held = node.workouts[index]
  if (held === undefined) {
    return null
  }
  let copy = held
  if (scheduleOf(node) === 'strict') {
    const free = freeDayAfter(node, dayOf(held))
    if (free === null) {
      return null
    }
    copy = withProp(held, 'day', number(free, UNITS.reps))
  }
  const named = { ...copy, name: `${held.name} copy` }
  const next = inDayOrder({
    ...node,
    workouts: [...node.workouts.slice(0, index + 1), named, ...node.workouts.slice(index + 1)],
  })
  return { node: next, at: next.workouts.indexOf(named) }
}

/** A workout put on a day of the cycle, swapping with whatever was already there. */
function onDay(node: TemplateNode, index: number, day: number): TemplateNode {
  const cycle = Math.max(1, cycleOf(node) ?? 7)
  if (day < 0 || day >= cycle) {
    return node
  }
  const taken = node.workouts.findIndex((workout) => dayOf(workout) === day)
  const was = dayOf(node.workouts[index] ?? blankWorkout(''))
  return {
    ...node,
    workouts: node.workouts.map((workout, position) => {
      if (position === index) {
        return withProp(workout, 'day', number(day, UNITS.reps))
      }
      if (position === taken && was !== null) {
        return withProp(workout, 'day', number(was, UNITS.reps))
      }
      return workout
    }),
  }
}

/** Dropping one workout on another swaps the days they sit on. */
function withDay(node: TemplateNode, from: number, to: number): TemplateNode {
  const day = dayOf(node.workouts[to] ?? blankWorkout(''))
  return day === null ? node : onDay(node, from, day)
}

/** A group of this kind, born with two empty exercises to fill in. */
function freshGroup(kind: GroupKind): GroupItem {
  return {
    type: 'group',
    kind,
    exercises: [blankExercise(''), blankExercise('')],
    props:
      kind === 'superset' ? [{ name: 'rest', value: number(90, UNITS.seconds), at: NOWHERE }] : [],
    comments: [],
    at: NOWHERE,
  }
}

/** Wraps an exercise in a choose beside a fresh, unnamed alternative. */
function wrapAlternative(workout: WorkoutNode, index: number): WorkoutNode {
  const item = workout.items[index]
  if (item?.type !== 'exercise') {
    return workout
  }
  const group: GroupItem = {
    type: 'group',
    kind: 'choose',
    exercises: [item.exercise, blankExercise('')],
    props: [],
    comments: [],
    at: NOWHERE,
  }
  return {
    ...workout,
    items: workout.items.map((held, position) => (position === index ? group : held)),
  }
}

function propSource(props: readonly Prop[], name: string): string | null {
  const found = props.find((prop) => prop.name === name)
  return found ? exprSource(found.value) : null
}

function text(value: string): Expr {
  return { type: 'text', value, at: NOWHERE }
}
